import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import {
  DeleteFacilityRole,
  GetFacilityAdmin,
  GetFacilityPermissions,
  GetFacilityRoles,
  IFacilityRole,
  IPermissionDetails,
  IPermissionDetailsList,
  PutFacilityRole,
} from "api/rpc/2024-04/facilityAdmin/client/admin/admin";

import Card from "components/card/Card";
import Page from "components/page/Page";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { useTranslation } from "react-i18next";
import RoleDetailsCard from "./components/RoleDetailsCard";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import { isEqualWith, isNull } from "lodash";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/pro-regular-svg-icons";
import Tabs from "components/tabs/Tabs";
import { IFacilityStaff } from "redux/reducers/models/facility";
import axios, { CancelToken } from "axios";
import DataTable from "components/dataTable/DataTable";
import { Badge } from "components/badge/Badge";
import { ButtonNew } from "components/buttonNew";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactDOM from "react-dom";
import PermissionEditor from "../permissions/PermissionEditor";
import ApplyRolePermissionsPopup from "./components/ApplyRolePermissionsPopup";

enum tabSelection {
  ROLES = 0,
  STAFF = 1,
}

interface IFilterState {
  offset: number;
}

const TABLE_LIMIT = 50;

export default function FacilityStaffDetailsEdit() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const tabs = [
    {
      id: "roles",
      content: t("Roles Permissions"),
    },
    {
      id: "staff",
      content: t("Staff Members"),
    },
  ];

  const [roleState, setRoleState] = useState<IFacilityRole>();
  const [roleStateBeforeChange, setRoleStateBeforeChanges] = useState<IFacilityRole>();
  const [adminData, setAdminData] = useState<Array<IFacilityStaff & {roles?: Array<{id: number; title: string; wage?: string}>}>>(undefined);
  const [filterState, setFilterState] = useState<IFilterState>({
    offset: 0,
  });

  const [permissionState, setPermissionState] = useState<IPermissionDetailsList>(null);
  const [selected, setSelected] = useState<number>(0);
  const [addWage, setAddWage] = useState<boolean>(false);

  const { state: deleteRolePopup, updateModal: updateDeleteRolePopup, closeModal: closeDeleteRolePopup } = useModal();
  const { state: duplicateRolePopup, closeModal: closeDuplicateRolePopup } = useModal();
  const { state: kioskViewPopup, updateModal: updateKioskViewPopup, closeModal: closeKioskViewPopup } = useModal();
  const {
    state: applyPermissionsPopup,
    updateModal: updateApplyPermissionsPopup,
    closeModal: closeApplyPermissionsPopup,
  } = useModal();

  useEffect(() => {
    void loadPermissionDetails();
    void loadFacilityRole();
    void loadFacilityStaff();
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadFacilityStaff(source.token);
    return () => source.cancel();
  }, [filterState.offset]);

  // API Calls
  async function loadPermissionDetails() {
    const permissionDetails = await GetFacilityPermissions({}, true);

    if (permissionDetails.status !== StatusCode.OK) {
      dispatch(showError(t("Error retrieving permissions")));
      return;
    }

    setPermissionState(permissionDetails.data);
  }

  async function loadFacilityStaff(token?: CancelToken) {
    if (adminData !== undefined) {
      setAdminData(undefined);
    }

    const res = await GetFacilityAdmin({roles: true}, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading staff accounts for the facility.")));
    }

    setAdminData(res.status !== StatusCode.OK ? [] : res.data);
  }

  async function loadFacilityRole() {
    const roleRes = await GetFacilityRoles({ id: Number(id), permissions: true }, true);

    if (roleRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading facility roles")));
      return;
    }

    setRoleState(roleRes.data[0]);
    if (roleRes?.data[0]?.wage != null) {
      setAddWage(true);
    }
  }

  async function deleteRole() {
    const deleteRoleRes = await DeleteFacilityRole({ id: roleState?.id }, true);
    if (deleteRoleRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting role from facility")));
      return;
    }

    dispatch(showSuccess(t("Role successfully deleted")));
    history.push("/admin/settings/staff/");
  }

  async function updateRole(applyToExistingUsers = false) {
    closeApplyPermissionsPopup();
    closeKioskViewPopup();

    const permissionsData = roleState.permissions?.map(permission => ({
      permission_detail_id: permission.id,
      value_type: "boolean",
      value: permission.pivot.boolean_value,
    }));

    const params = {
      id: roleState?.id,
      title: roleState?.title,
      description: roleState?.description,
      default: roleState?.default,
      permissions: permissionsData,
      wage: addWage ? roleState?.wage : null,
      apply_to_existing_users: applyToExistingUsers,
    };

    const updateRoleRes = await PutFacilityRole(params, true);
    if (updateRoleRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating facility role")));
      return;
    }

    dispatch(showSuccess(t("Facility role successfully updated")));

    history.push("/admin/settings/staff/");
  }

  async function duplicateRole() { }

  // End API Calls

  // Handlers
  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  };

  function setRolePermission(permission: IPermissionDetails, checked: boolean) {
    setRoleState(prev => {
      if (!prev) {
        return prev;
      }

      const exists = prev.permissions?.some(item => item.id === permission.id);
      const permissions = exists
        ? prev.permissions.map(item =>
            item.id === permission.id
              ? { ...item, pivot: { ...item.pivot, boolean_value: checked, permission_id: permission.id } }
              : item,
          )
        : [
            ...(prev.permissions || []),
            { ...permission, pivot: { permission_id: permission.id, boolean_value: checked } },
          ];

      return { ...prev, permissions };
    });
  }

  function setRolePermissions(permissionList: IPermissionDetails[], checked: boolean) {
    const ids = new Set(permissionList.map(permission => permission.id));

    setRoleState(prev => {
      if (!prev) {
        return prev;
      }

      const updated = (prev.permissions || []).map(permission =>
        ids.has(permission.id)
          ? { ...permission, pivot: { ...permission.pivot, boolean_value: checked } }
          : permission,
      );
      const existingIds = new Set(updated.map(permission => permission.id));
      const missing = permissionList
        .filter(permission => !existingIds.has(permission.id))
        .map(permission => ({ ...permission, pivot: { permission_id: permission.id, boolean_value: checked } }));

      return { ...prev, permissions: [...updated, ...missing] };
    });
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset - TABLE_LIMIT }));
        return;
      }
      case "next": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset + TABLE_LIMIT }));
        return;
      }
      default:
        return;
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    const { id, value } = e.target;
    setRoleState(e => ({ ...e, [id]: value }));
  }

  function handleToggle(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = e.target;

    setRoleState(e => ({ ...e, [id]: checked }));
  }
  // End Handlers

  function roleHasKioskViewEnabled() {
    return (
      (roleState?.permissions ?? []).some(
        permission => permission?.key === "kiosk_view" && !!permission?.pivot?.boolean_value,
      ) ?? false
    );
  }

  function permissionSignature(role?: IFacilityRole) {
    return (role?.permissions ?? [])
      .map(permission => `${permission.id}:${permission.pivot?.boolean_value ? 1 : 0}`)
      .sort()
      .join("|");
  }

  function permissionsChanged() {
    if (!roleStateBeforeChange) {
      return false;
    }

    return permissionSignature(roleStateBeforeChange) !== permissionSignature(roleState);
  }

  function askToApplyPermissions() {
    if (permissionsChanged()) {
      updateApplyPermissionsPopup({ isOpen: true });
      return;
    }

    void updateRole(false);
  }

  function handleSaveRole() {
    if (roleHasKioskViewEnabled()) {
      void updateKioskViewPopup({ isOpen: true });
      return;
    }
    askToApplyPermissions();
  }

  function checkStatus(permission: IPermissionDetails) {
    return roleState?.permissions?.some(item => item.id === permission.id && item.pivot.boolean_value) ?? false;
  }

  function unsavedChangesExist() {
    if (roleStateBeforeChange === undefined) {
      if (roleState) {
        setRoleStateBeforeChanges(roleState);
      }
      return false;
    }

    return !isEqualWith(roleStateBeforeChange, roleState, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    setRoleState(roleStateBeforeChange);
  }

  return (
    <Page
      title={roleState?.title || undefined}
      subtitle={t("Set up access permissions and manage staff assignments for this role.")}
      breadcrumbs={[
        { label: t("Settings") },
        { label: t("Staff Accounts"), url: "/admin/settings/staff" },
        {
          label: roleState?.title || t("Role"),
          url: `/admin/settings/roles_edit/${id}`,
        },
      ]}
      multipleActionDropdownAction={{
        label: t("More Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Delete"),
              handler: () => updateDeleteRolePopup({ isOpen: true }),
              disabled: roleState?.locked,
              icon: "trash-can",
            },
          ],
        },
      }}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: handleSaveRole,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <Page.Section>
        <div style={{ display: "flex", overflowX: "auto", paddingBottom: 24, paddingTop: 12 }}>
          <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} fullWidth variant="wide" />
        </div>
      </Page.Section>
      {(selected as tabSelection) === tabSelection.ROLES ? (
        <div className="mb-10">
          <RoleDetailsCard addWage={addWage} updateAddWage={() => {
            ReactDOM.unstable_batchedUpdates(() => {
              setAddWage(prev => !prev);
              setRoleState(prev => ({...prev, wage: ""}));
            });
            
          }} roleDetails={roleState} inputChange={handleInputChange} toggleChange={handleToggle} />
          <PermissionEditor
            permissions={permissionState}
            title={t("Role Permissions")}
            disabled={roleState?.locked}
            isChecked={checkStatus}
            onToggle={setRolePermission}
            onToggleMany={setRolePermissions}
          />
        </div>
      ) : null}

      {(selected as tabSelection) === tabSelection.STAFF ? (
        <>
          <Card
            title={
              <div className="flex items-center gap-2">
                {t("Staff Accounts")}{" "}
                <Badge type={"public"} size="medium">
                  {adminData?.filter(admin => admin?.state !== "disabled" && admin?.roles?.some(role => role?.id === roleState?.id)).length}
                </Badge>
              </div>
            }
            titleActions={[
              {
                content: t("+ New Staff Account"),
                action: () => history.push("/admin/settings/staff_new"),
                buttonType: "primary",
              },
            ]}
          >
            <div className="pt-3">
              <DataTable
                columns={[
                  { label: t("Name"), width: "25%" },
                  { label: t("Username"), width: "25%" },
                  { label: t("Role"), width: "25%" },
                  { label: "" },
                ]}
                footer={{
                  tableLimit: TABLE_LIMIT,
                  tableOffset: filterState.offset,
                  handleTableOffset: direction => handleTableNavigation(direction),
                  disableNextOffset: !adminData || !(adminData?.length === TABLE_LIMIT),
                }}
              >
                {adminData
                  ?.filter(admin => admin?.state !== "disabled" && admin?.roles?.some(role => role?.id === roleState?.id))
                  .map(admin => {
                    const adminRoles = admin?.roles?.filter((_, index) => index < 3);
                    return (
                      <tr
                        key={admin.id}
                        className="clickable"
                        onClick={() => history.push("/admin/settings/staff/" + String(admin.id))}
                      >
                        <td>{admin.full_name}</td>
                        <td>{admin.username}</td>
                        <td>{adminRoles?.map(role => role?.title)?.join(", ")} {admin?.roles?.length > 3 ?
                      <OverlayTrigger
                      placement="auto"
                      overlay={<Tooltip id="staff-roles">{admin?.roles?.slice(3)?.map(role => role?.title)?.join(", ")}</Tooltip>}
                    ><span><Badge type="gray" size="small">{t("More")}</Badge></span></OverlayTrigger>
                      : null}</td>
                        <td align="right">
                          <NavigationDropdownNew
                            showPlainTextLabel
                            rightAlign
                            label={
                              <div>
                                <ButtonNew style={{ padding: 15 }} size="medium" type="text">
                                  <FontAwesomeIcon icon={faEllipsisV} />
                                </ButtonNew>
                              </div>
                            }
                            sections={[
                              [
                                {
                                  label: t("Edit Staff"),
                                  icon: "pen",
                                  onClick: () => history.push("/admin/settings/staff/" + String(admin.id)),
                                },
                              ],
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
              </DataTable>
            </div>
          </Card>
        </>
      ) : null}

      <Portal isMounted={deleteRolePopup.isOpen}>
        <div className="pricing-sheets-products-modal">
          <Popup
            closable
            title={t("Delete Role?")}
            description={t("Are you sure you want to delete this role? This is permanent.")}
            open={deleteRolePopup.isOpen}
            onOk={deleteRole}
            onCancel={closeDeleteRolePopup}
            type={"warning"}
          />
        </div>
      </Portal>

      <Portal isMounted={duplicateRolePopup.isOpen}>
        <Popup
          open={duplicateRolePopup.isOpen}
          closable
          title={t("Duplicate Role?")}
          description={t("Are you sure you want to duplicate this role and permissions?")}
          onOk={duplicateRole}
          onCancel={closeDuplicateRolePopup}
          type="info"
        />
      </Portal>

      <Popup
        open={kioskViewPopup?.isOpen}
        type="info"
        title={t("Kiosk View Permission Set")}
        description={t("This role will only have access to the kiosk view. Are you sure you want to continue?")}
        onOk={() => {
          closeKioskViewPopup();
          askToApplyPermissions();
        }}
        okText={t("Continue")}
        cancelText={t("Cancel")}
        onCancel={closeKioskViewPopup}
      />

      <ApplyRolePermissionsPopup
        open={applyPermissionsPopup.isOpen}
        onYes={() => void updateRole(true)}
        onNo={() => void updateRole(false)}
      />
    </Page>
  );
}
